import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { CheckoutSession } from "@/types";
import { PaymentMethodsView } from "@/modules/payment-methods/views/payment-methods-view";
import { useCheckoutSessionV3 } from "@/modules/checkout-session/api-hooks/use-checkout-session";
import { emitSinglePageCheckoutShownTracking } from "@/shared/tracking/analytics-single-page-events";
import { selectPreSelectedOption } from "../lib/select-pre-selected-option";
import { PreSelectedPlanSummary } from "../components/pre-selected-plan-summary";
import { SinglePagePerksBanner } from "../components/single-page-perks-banner";
import { Processing } from "@/modules/payment-methods/types";

interface SinglePageCheckoutViewProps {
  onSessionFetched: (session: CheckoutSession) => void;
}

/**
 * Thin composition view for Single Page Checkout. Renders the read-only
 * pre-selected plan summary above the existing payment flow via
 * PaymentMethodsView's topSlot render-prop, and fires the
 * single_page_checkout_shown event once per distinct session.
 */
export function SinglePageCheckoutView({
  onSessionFetched,
}: SinglePageCheckoutViewProps) {
  const { t } = useTranslation();
  const { data: session } = useCheckoutSessionV3();
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (session && firedForRef.current !== session.id) {
      firedForRef.current = session.id;
      emitSinglePageCheckoutShownTracking(session);
    }
  }, [session]);

  // Per Figma, every single-page variant heads the payment section
  // "Payment method" with no "Pay X today"/refund block — the read-only
  // top summary already states the plan, amount, and the refund line.
  return (
    <PaymentMethodsView
      hideBack
      onSessionFetched={onSessionFetched}
      paymentHeading={t("single_page_checkout.payment_method")}
      hideCancelMessage
      topSlot={(ctx, { isWalletSelected }) => {
        const option = selectPreSelectedOption(ctx.session);
        return option ? (
          <PreSelectedPlanSummary
            ctx={ctx}
            option={option}
            isWalletSelected={isWalletSelected}
          />
        ) : null;
      }}
      footerAbovePaySlot={(ctx, { processing }) => {
        const option = selectPreSelectedOption(ctx.session);
        return option ? (
          <SinglePagePerksBanner
            ctx={ctx}
            option={option}
            disabled={processing !== Processing.NONE}
          />
        ) : null;
      }}
    />
  );
}
